// src/components/captures/CaptureViewModal.tsx
import React, { useState } from 'react';
import { Capture } from '../../types/capture';
import ResponsiveModal from '../common/ResponsiveModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { formatRelativeTime, toDateObject } from '../../utils/dateUtils';
import { ArrowLeft, MoreVertical, Edit, Trash, Archive, ArchiveRestore, Sparkles } from 'lucide-react';

interface CaptureViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    capture: Capture;
    onEdit: () => void;
    onDelete: () => void;
    onToggleArchive: () => void;
    onConvertToTask: () => void;
}

const CaptureViewModal: React.FC<CaptureViewModalProps> = ({
    isOpen,
    onClose,
    capture,
    onEdit,
    onDelete,
    onToggleArchive,
    onConvertToTask
}) => {
    // More 選單狀態
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    // 刪除操作的確認狀態
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // 處理刪除操作
    const handleDelete = () => {
        setShowDeleteConfirm(false);
        setShowMoreMenu(false);
        onDelete();
    };

    // 處理編輯操作
    const handleEdit = () => {
        setShowMoreMenu(false);
        onEdit();
    };

    // 處理 Archive 操作
    const handleArchive = () => {
        onToggleArchive();
    };

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={onClose}
            title="" // 不使用預設標題
            showHeader={false} // 完全自訂頂部
        >
            {/* 使用 flex 佈局確保操作區置底 */}
            <div className="h-full flex flex-col">
                {/* 自訂頂部導航 */}
                <div className="shrink-0 sticky top-0 bg-white z-10 border-b border-gray-100">
                    <div className="flex items-center justify-between px-4 py-3">
                        {/* 左側：返回按鈕 */}
                        <button
                            className="p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
                            onClick={onClose}
                            aria-label="back"
                            type="button"
                        >
                            <ArrowLeft size={20} className="text-gray-600" />
                        </button>

                        {/* 中間：時間戳 */}
                        <span className="text-xs text-gray-400">
                            {formatRelativeTime(toDateObject(capture.createdAt))}
                        </span>

                        {/* 右側：More 選單 */}
                        <div className="relative">
                            <button
                                className="p-2 -mr-2 hover:bg-gray-100 rounded-lg transition-colors"
                                onClick={() => setShowMoreMenu(!showMoreMenu)}
                                aria-label="more options"
                                type="button"
                            >
                                <MoreVertical size={20} className="text-gray-500" />
                            </button>

                            {/* More 下拉選單 */}
                            {showMoreMenu && (
                                <>
                                    {/* 點擊外部關閉選單 */}
                                    <div
                                        className="fixed inset-0"
                                        onClick={() => setShowMoreMenu(false)}
                                    />

                                    <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg">
                                        <div className="py-1">
                                            <button
                                                onClick={handleEdit}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                                type="button"
                                            >
                                                <Edit size={16} />
                                                <span>Edit</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowMoreMenu(false);
                                                    setShowDeleteConfirm(true);
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                type="button"
                                            >
                                                <Trash size={16} />
                                                <span>Delete</span>
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* 主要內容區域 - flex-1 撐滿剩餘空間，可滾動 */}
                <div className="flex-1 px-5 py-4 overflow-y-auto hide-scrollbar">
                    <div className="prose max-w-none">
                        <div className="whitespace-pre-wrap text-gray-800 text-lg sm:text-xl leading-relaxed">
                            {capture.content}
                        </div>
                    </div>
                </div>

                {/* 底部操作區 - 固定在底部 */}
                <div className="shrink-0 border-t border-gray-100 px-3 py-2 sm:py-4 bg-white">
                    {/* 未轉換狀態：To Task + Archive 兩個按鈕 */}
                    {!capture.convertedToId && (
                        <div className="flex flex-row gap-3">
                            {/* To Task 主要按鈕 */}
                            <button
                                onClick={onConvertToTask}
                                className="btn-primary flex-1"
                                type="button"
                            >
                                <Sparkles size={20} />
                                <span>To Task</span>
                            </button>

                            {/* Archive/Restore 按鈕 - Ghost 樣式圖標按鈕 */}
                            <button
                                onClick={handleArchive}
                                className="w-14 h-14 sm:w-12 sm:h-12 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all"
                                type="button"
                                aria-label={capture.processed ? 'Restore' : 'Archive'}
                            >
                                {capture.processed ? (
                                    <ArchiveRestore size={26} className="sm:w-5 sm:h-5" />
                                ) : (
                                    <Archive size={26} className="sm:w-5 sm:h-5" />
                                )}
                            </button>
                        </div>
                    )}

                    {/* 已轉換狀態：只顯示提示 + Restore 按鈕 */}
                    {capture.convertedToId && (
                        <>
                            <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm text-green-800 text-center">
                                    ✓ Converted to Task
                                </p>
                            </div>

                            {/* Restore 按鈕（已轉換後通常是 Archive 狀態，所以這裡是 Restore） */}
                            <button
                                onClick={handleArchive}
                                className="w-full py-3 px-4 text-gray-600 hover:bg-gray-50 rounded-xl transition-colors font-medium flex items-center justify-center gap-2 "
                                type="button"
                            >
                                {capture.processed ? <ArchiveRestore size={18} /> : <Archive size={18} />}
                                <span>{capture.processed ? 'Restore' : 'Archive'}</span>
                            </button>
                        </>
                    )}

                    {/* Safe area padding for mobile */}
                    <div className="h-2"></div>
                </div>
            </div>

            {/* 刪除確認對話框 */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                type="delete"
                title="Confirm Delete?"
                message="This action cannot be undone."
                confirmText="Delete"
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </ResponsiveModal>
    );
};

export default CaptureViewModal;
